import type { ComponentProps } from 'react'

import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'

type IconButtonProps = Omit<ComponentProps<typeof Button>, 'size' | 'variant'> & {
  /**
   * Required: an icon-only control has no visible label, so this is the only
   * name a screen reader or a tooltip can use.
   */
  label: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  variant?: 'secondary' | 'ghost'
  tone?: 'neutral' | 'danger'
}

const SIZES = {
  xs: 'icon-xs',
  sm: 'icon-sm',
  md: 'icon',
  lg: 'icon-lg',
} as const

/**
 * A square Button. Kept as its own component so the accessible name cannot be
 * forgotten and so the size ladder maps to the icon scale in one place.
 */
export function IconButton({
  label,
  size = 'md',
  variant = 'secondary',
  tone = 'neutral',
  className,
  ...props
}: IconButtonProps) {
  return (
    <Button
      aria-label={label}
      title={label}
      size={SIZES[size]}
      variant={variant}
      className={cn(
        'text-fg-secondary hover:text-foreground',
        tone === 'danger' && 'hover:bg-destructive/14 hover:text-destructive-text',
        className,
      )}
      {...props}
    />
  )
}
